import { useState } from 'react';
import { ShieldAlert, ChevronDown, ChevronUp } from 'lucide-react';
import { useRiscoEvasao } from '../../api/hooks/useRiscoEvasao';

/**
 * Card reutilizável que exibe resumo de alunos em risco de evasão.
 *
 * Usado em: DashboardEscola, EquipeDashboard
 *
 * Props:
 * - expanded: mostra lista detalhada por padrão
 * - onSelectAluno: callback para drill-down (navegar para ficha do aluno)
 */
export function AlunosEmRiscoCard({
  expanded = false,
  onSelectAluno,
  compact = false,
}: {
  expanded?: boolean;
  onSelectAluno?: (alunoId: string) => void;
  /** Layout mais denso (ex.: dashboard escola) */
  compact?: boolean;
}) {
  const { resumo, alunos, isLoading } = useRiscoEvasao();
  const [filtroNivel, setFiltroNivel] = useState<string | null>(null);
  const [showLista, setShowLista] = useState(expanded);

  const padHeader = compact ? '10px 12px' : '14px 16px';
  const padSection = compact ? '10px 12px' : '14px 16px';
  const titleFs = compact ? '0.8rem' : '0.95rem';
  const badgePad = compact ? '4px 8px' : '5px 10px';
  const countFs = compact ? '0.78rem' : '0.85rem';
  const labelFs = compact ? '0.62rem' : '0.7rem';

  if (isLoading) {
    return (
      <div style={{
        background: 'white', borderRadius: compact ? 10 : 12, border: '1px solid #e5e7eb',
        padding: compact ? 14 : 24, minHeight: compact ? 72 : 120,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{ color: '#9ca3af', fontSize: compact ? '0.72rem' : '0.85rem' }}>Carregando análise de risco...</span>
      </div>
    );
  }

  const niveis = [
    { key: 'EVASAO', label: 'Evasão', count: resumo.evasao, color: '#1e1b4b', bg: '#eef2ff', borderColor: '#c7d2fe' },
    { key: 'CRITICO', label: 'Crítico', count: resumo.critico, color: '#991b1b', bg: '#fef2f2', borderColor: '#fecaca' },
    { key: 'ALERTA', label: 'Alerta', count: resumo.alerta, color: '#92400e', bg: '#fefce8', borderColor: '#fde68a' },
    { key: 'ATENCAO', label: 'Atenção', count: resumo.atencao, color: '#1e40af', bg: '#eff6ff', borderColor: '#bfdbfe' },
  ];

  const temRisco = resumo.total > 0;

  const alunosFiltrados = filtroNivel
    ? alunos.filter((a) => a.nivel === filtroNivel)
    : alunos;

  const handleBadgeClick = (key: string, count: number) => {
    if (count === 0) return;
    if (filtroNivel === key) {
      setFiltroNivel(null);
    } else {
      setFiltroNivel(key);
      setShowLista(true);
    }
  };

  return (
    <div style={{
      background: 'white', borderRadius: compact ? 10 : 12, overflow: 'hidden',
      border: `1px solid ${temRisco ? '#fecaca' : '#e5e7eb'}`,
    }}>
      {/* Header: título | filtros | ocultar */}
      <div style={{
        padding: padHeader,
        background: temRisco
          ? 'linear-gradient(135deg, #fef2f2 0%, #fff7ed 100%)'
          : '#fafafa',
        borderBottom: temRisco && showLista ? '1px solid #e5e7eb' : 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: compact ? 8 : 12,
        flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 8 : 10, minWidth: 0 }}>
          <ShieldAlert size={compact ? 15 : 18} style={{ color: temRisco ? '#dc2626' : '#9ca3af', flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <h3 style={{ fontSize: titleFs, fontWeight: 700, margin: 0, lineHeight: 1.25 }}>
              Alunos em Risco de Evasão
            </h3>
            {temRisco && (
              <p style={{ margin: '2px 0 0', fontSize: compact ? '0.68rem' : '0.75rem', color: '#dc2626', fontWeight: 600 }}>
                {resumo.total} aluno{resumo.total !== 1 ? 's' : ''} em acompanhamento
                {filtroNivel ? ` · filtro ${filtroNivel}` : ''}
              </p>
            )}
            {!temRisco && (
              <p style={{ margin: '2px 0 0', fontSize: compact ? '0.68rem' : '0.75rem', color: '#6b7280' }}>
                Nenhum aluno em risco identificado
              </p>
            )}
          </div>
        </div>

        {temRisco && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: compact ? 6 : 8,
            flexWrap: 'wrap',
            marginLeft: 'auto',
          }}>
            {niveis.map((n) => {
              const ativo = filtroNivel === n.key;
              const clicavel = n.count > 0;
              return (
                <button
                  key={n.key}
                  type="button"
                  onClick={() => handleBadgeClick(n.key, n.count)}
                  disabled={!clicavel}
                  title={clicavel ? (ativo ? 'Remover filtro' : `Filtrar: ${n.label}`) : undefined}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: badgePad,
                    borderRadius: 999,
                    background: n.count > 0 ? n.bg : '#f3f4f6',
                    border: `1.5px solid ${ativo ? n.color : n.count > 0 ? n.borderColor : '#e5e7eb'}`,
                    opacity: n.count > 0 ? 1 : 0.55,
                    cursor: clicavel ? 'pointer' : 'default',
                    fontWeight: 700,
                    lineHeight: 1.2,
                    boxShadow: ativo ? `0 0 0 1px ${n.color}` : undefined,
                  }}
                >
                  <span style={{
                    fontSize: countFs,
                    color: n.count > 0 ? n.color : '#9ca3af',
                  }}>
                    {n.count}
                  </span>
                  <span style={{
                    fontSize: labelFs,
                    color: n.count > 0 ? n.color : '#9ca3af',
                  }}>
                    {n.label}
                  </span>
                </button>
              );
            })}

            <button
              type="button"
              onClick={() => setShowLista(!showLista)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px 2px 4px 6px',
                color: '#6b7280',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                fontSize: compact ? '0.68rem' : '0.75rem',
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {showLista ? 'Ocultar' : 'Expandir'}
              {showLista ? <ChevronUp size={compact ? 12 : 14} /> : <ChevronDown size={compact ? 12 : 14} />}
            </button>
          </div>
        )}
      </div>

      {/* Lista (só quando expandido) */}
      {temRisco && showLista && (
        <div style={{ padding: padSection }}>
          {alunosFiltrados.length === 0 ? (
            <p style={{ margin: 0, fontSize: '0.82rem', color: '#6b7280', textAlign: 'center' }}>
              Nenhum aluno neste filtro.
            </p>
          ) : (
            <div>
              <div style={{
                fontSize: '0.78rem', fontWeight: 700, color: '#6b7280', marginBottom: 8,
                textTransform: 'uppercase', letterSpacing: '0.05em',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span>Detalhamento</span>
                <span style={{ fontWeight: 400, textTransform: 'none' }}>
                  {alunosFiltrados.length} aluno{alunosFiltrados.length !== 1 ? 's' : ''}
                  {filtroNivel && (
                    <button
                      type="button"
                      onClick={() => setFiltroNivel(null)}
                      style={{
                        marginLeft: 8, background: 'none', border: 'none',
                        color: '#3b82f6', cursor: 'pointer', fontSize: '0.75rem',
                        fontWeight: 600, textDecoration: 'underline',
                      }}
                    >
                      Limpar filtro
                    </button>
                  )}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {alunosFiltrados.slice(0, 20).map((item, idx) => {
                  const nivelInfo = niveis.find((n) => n.key === item.nivel);
                  return (
                    <div
                      key={idx}
                      onClick={() => onSelectAluno?.(item.aluno.id)}
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '8px 12px', borderRadius: 8, fontSize: '0.82rem',
                        background: nivelInfo?.bg ?? '#fafafa',
                        border: `1px solid ${nivelInfo?.borderColor ?? '#f3f4f6'}`,
                        cursor: onSelectAluno ? 'pointer' : 'default',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={(e) => { if (onSelectAluno) e.currentTarget.style.opacity = '0.85'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                    >
                      <div>
                        <span style={{ fontWeight: 600, color: '#1f2937' }}>{item.aluno.nome}</span>
                        {item.turma && (
                          <span style={{ marginLeft: 8, fontSize: '0.72rem', color: '#6b7280' }}>
                            {item.turma.nome} • {item.turma.turno}
                          </span>
                        )}
                        {item.descricao && (
                          <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: 2 }}>
                            {item.descricao}
                          </div>
                        )}
                      </div>
                      <span style={{
                        padding: '2px 10px', borderRadius: 6,
                        fontSize: '0.7rem', fontWeight: 700,
                        background: nivelInfo?.bg, color: nivelInfo?.color,
                        border: `1px solid ${nivelInfo?.borderColor}`,
                      }}>
                        {item.nivel}
                      </span>
                    </div>
                  );
                })}
                {alunosFiltrados.length > 20 && (
                  <p style={{ fontSize: '0.75rem', color: '#9ca3af', textAlign: 'center', margin: '4px 0 0' }}>
                    +{alunosFiltrados.length - 20} aluno(s) não exibido(s)
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
